import React, { useEffect } from 'react';
import { Grid, TextField, Typography, Box, Divider } from '@mui/material';
import { UtilityService, UtilityType } from '../../../types/utility.types';

// Helper function to format number for display in text field
const formatNumberForInput = (value: number | string | undefined): string => {
  if (value === undefined || value === null) return '';
  return value.toString();
};

interface EnergyFormProps {
  formData: UtilityService;
  handleFormChange: (field: string, value: any) => void;
  handleNestedChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isViewMode: boolean;
}

/**
 * Component for Gas and Electricity contracts
 */
const EnergyForm: React.FC<EnergyFormProps> = ({
  formData,
  handleFormChange,
  handleNestedChange,
  isViewMode,
}) => {
  const isGas = formData.serviceType === 'Gas';
  
  // Calculate contract value when relevant fields change
  useEffect(() => {
    if (formData.eacAq && formData.uplift !== undefined && formData.contractStartDate && formData.contractEndDate) {
      try {
        // Parse values
        const eacAq = parseFloat(formData.eacAq as string);
        const uplift = typeof formData.uplift === 'number' 
          ? formData.uplift 
          : parseFloat(formData.uplift as string);
        
        // Calculate contract duration in years
        const startDate = new Date(formData.contractStartDate as string);
        const endDate = new Date(formData.contractEndDate as string);
        const durationInDays = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
        const durationInYears = durationInDays / 365.25;
        
        // Calculate contract value - convert uplift from pence to pounds (divide by 100)
        const contractValue = (eacAq * (uplift / 100) * durationInYears).toFixed(2);
        
        // Update contract value
        handleFormChange('contractValue', contractValue);
      } catch (error) {
        console.error('Error calculating contract value:', error);
      }
    }
  }, [formData.eacAq, formData.uplift, formData.contractStartDate, formData.contractEndDate]);
  
  return (
    <Grid container spacing={3}>
      {/* MPRN/MPAN/SPID */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label={isGas ? 'MPRN' : 'MPAN'}
          name="meterDetails.mpanMprnSpid"
          value={formData.meterDetails?.mpanMprnSpid || ''}
          onChange={handleNestedChange}
          disabled={Boolean(isViewMode)}
          required
          helperText={isGas ? 'Meter Point Reference Number' : 'Meter Point Administration Number'}
        />
      </Grid>
      
      {/* Meter Serial Number */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Meter Serial Number"
          name="meterDetails.serialNumber"
          value={formData.meterDetails?.serialNumber || ''}
          onChange={handleNestedChange}
          disabled={Boolean(isViewMode)}
          helperText="Physical meter serial number"
        />
      </Grid>
      
      {/* Meter Type (for backward compatibility) */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Meter Type"
          name="meterDetails.meterType"
          value={formData.meterDetails?.meterType || ''}
          onChange={handleNestedChange}
          disabled={Boolean(isViewMode)}
        />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>Rate Information</Typography>
      </Grid>
      
      {/* Day Rate */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Day Rate (p/kWh)"
          name="dayRate"
          value={formatNumberForInput(formData.dayRate)}
          onChange={(e) => handleFormChange('dayRate', e.target.value)}
          disabled={Boolean(isViewMode)}
          type="number"
          inputProps={{ step: '0.0001' }}
          required
          helperText="Day unit rate in pence per kWh"
        />
      </Grid>

      {/* Day Consumption */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Day Consumption (kWh)"
          name="dayConsumption"
          value={formatNumberForInput(formData.dayConsumption)}
          onChange={(e) => handleFormChange('dayConsumption', e.target.value)}
          disabled={Boolean(isViewMode)}
          type="number"
          required
          helperText="Day consumption in kilowatt-hours"
        />
      </Grid>

      {/* Night Rate */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Night Rate (p/kWh)"
          name="nightRate"
          value={formatNumberForInput(formData.nightRate)}
          onChange={(e) => handleFormChange('nightRate', e.target.value)}
          disabled={Boolean(isViewMode)}
          type="number"
          inputProps={{ step: '0.0001' }}
          helperText="Night unit rate in pence per kWh"
        />
      </Grid>

      {/* Night Consumption */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Night Consumption (kWh)"
          name="nightConsumption"
          value={formatNumberForInput(formData.nightConsumption)}
          onChange={(e) => handleFormChange('nightConsumption', e.target.value)}
          disabled={Boolean(isViewMode)}
          type="number"
          helperText="Night consumption in kilowatt-hours"
        />
      </Grid>

      {/* Weekend Rate */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Weekend Rate (p/kWh)"
          name="weekendRate"
          value={formatNumberForInput(formData.weekendRate)}
          onChange={(e) => handleFormChange('weekendRate', e.target.value)}
          disabled={Boolean(isViewMode)}
          type="number"
          inputProps={{ step: '0.0001' }}
          helperText="Weekend unit rate in pence per kWh"
        />
      </Grid>

      {/* Weekend Consumption */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Weekend Consumption (kWh)"
          name="weekendConsumption"
          value={formatNumberForInput(formData.weekendConsumption)}
          onChange={(e) => handleFormChange('weekendConsumption', e.target.value)}
          disabled={Boolean(isViewMode)}
          type="number"
          helperText="Weekend consumption in kilowatt-hours"
        />
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>Contract Details</Typography>
      </Grid>
      
      {/* EAC/AQ */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="EAC/AQ"
          name="eacAq"
          value={formData.eacAq || ''}
          onChange={(e) => handleFormChange('eacAq', e.target.value)}
          disabled={Boolean(isViewMode)}
          helperText="Annual consumption in kWh"
          required
        />
      </Grid>
      
      {/* Standing Charge */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Standing Charge (p/day)"
          name="standingCharge"
          value={formatNumberForInput(formData.standingCharge)}
          onChange={(e) => handleFormChange('standingCharge', e.target.value)}
          disabled={Boolean(isViewMode)}
          type="number"
          inputProps={{ step: '0.01' }}
          required
          helperText="Daily standing charge in pence"
        />
      </Grid>
      
      {/* Uplift */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Uplift (p)"
          name="uplift"
          value={formatNumberForInput(formData.uplift)}
          onChange={(e) => handleFormChange('uplift', e.target.value)}
          disabled={Boolean(isViewMode)}
          type="number"
          inputProps={{ step: '0.01' }}
          helperText="Commission uplift in pence"
        />
      </Grid>
      
      {/* Contract Value (calculated) */}
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Contract Value"
          name="contractValue"
          value={formData.contractValue ? `£${formData.contractValue}` : ''}
          disabled={true}
          helperText="EAC/AQ * Uplift * Contract Duration"
        />
      </Grid>
    </Grid>
  );
};

export default EnergyForm;
